'use client';

/**
 * Base publique des appels API, telle que définie par NEXT_PUBLIC_API_URL.
 * En Docker, c'est nginx :80 (http://localhost/api/v1) ; en dev local sans Docker,
 * c'est le serveur PHP `php artisan serve` (http://localhost:8000/api/v1). La valeur
 * de l'environnement est respectée telle quelle ; le CORS backend autorise déjà :4028.
 */

function resolvePublicApiBase(): string {
  const fallback = '/api/v1';

  const fromEnv =
    typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_URL
      ? String(process.env.NEXT_PUBLIC_API_URL).trim()
      : '';

  const raw = fromEnv || fallback;

  // Relative URL: keep it relative so the browser uses the current domain.
  if (raw.startsWith('/')) {
    return raw.replace(/\/+$/, '');
  }

  // Absolute URL.
  try {
    const u = new URL(raw);
    return u.href.replace(/\/+$/, '');
  } catch {
    return fallback;
  }
}

export const API_URL = resolvePublicApiBase();

/** Base `/api` (sans `/v1`) pour les routes publiques hors préfixe versionné. */
export const API_ROOT = API_URL.replace(/\/v1\/?$/, '');

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export class ApiError extends Error {
  status: number;
  payload?: unknown;
  /** Secondes avant nouvel essai (429), issu de l’en-tête Retry-After si présent. */
  retryAfter?: number;

  constructor(message: string, status: number, payload?: unknown, retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.retryAfter = retryAfter;
  }
}

function joinUrl(base: string, path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const b = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

async function clearStaleSessionCookie(): Promise<void> {
  try {
    await fetch(joinUrl(API_URL, '/auth/forgot-session-cookie'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: '{}',
    });
  } catch {
    /* ignore */
  }
}

function getPayloadMessage(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  if (!('message' in payload)) return undefined;
  const message = (payload as Record<string, unknown>).message;
  return typeof message === 'string' ? message : undefined;
}

async function request<TResponse>(
  method: HttpMethod,
  url: string,
  body?: unknown
): Promise<TResponse> {
  const res = await fetch(joinUrl(API_URL, url), {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401) {
    await clearStaleSessionCookie();
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }
    throw new ApiError('Non authentifié', 401);
  }

  const text = await res.text();
  const payload = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;

  if (!res.ok) {
    const message = getPayloadMessage(payload) ?? `Erreur API (${res.status})`;
    let retryAfter: number | undefined;
    if (res.status === 429) {
      const ra = res.headers.get('Retry-After');
      if (ra && /^\d+$/.test(ra.trim())) retryAfter = parseInt(ra.trim(), 10);
      else retryAfter = 60;
    }
    throw new ApiError(message, res.status, payload, retryAfter);
  }

  return payload as TResponse;
}

export function get<TResponse = unknown>(url: string) {
  return request<TResponse>('GET', url);
}

export function post<TResponse = unknown, TBody = unknown>(url: string, body: TBody) {
  return request<TResponse>('POST', url, body);
}

/**
 * POST JSON sans en-tête Authorization (cookie de session envoyée automatiquement).
 */
export async function postPublic<TResponse = unknown, TBody = unknown>(
  absolutePathFromApiRoot: string,
  body: TBody
): Promise<TResponse> {
  const url = joinUrl(API_ROOT, absolutePathFromApiRoot);
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  const payload = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;

  if (!res.ok) {
    const message = getPayloadMessage(payload) ?? `Erreur API (${res.status})`;
    throw new ApiError(message, res.status, payload);
  }

  return payload as TResponse;
}

export function put<TResponse = unknown, TBody = unknown>(url: string, body: TBody) {
  return request<TResponse>('PUT', url, body);
}

export function patch<TResponse = unknown, TBody = unknown>(url: string, body: TBody) {
  return request<TResponse>('PATCH', url, body);
}

export function del<TResponse = unknown>(url: string) {
  return request<TResponse>('DELETE', url);
}

/**
 * En-têtes pour fetch manuels (ex. FormData). La session repose sur le cookie HTTP-only `bdl_token` :
 * utiliser `credentials: 'include'` ; ne pas envoyer `Authorization` depuis le client.
 */
export function getAuthHeaders(): Record<string, string> {
  return {};
}
